import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFuzzySearch } from '../../../hooks/useFuzzySearch';

interface ProductSelectorProps {
  allItems: Array<{ name: string }>;
  selectedItems: Set<string>;
  onToggleItem: (itemName: string) => void;
  getItemColor?: (itemName: string) => string;
  placeholder?: string;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  allItems,
  selectedItems,
  onToggleItem,
  getItemColor,
  placeholder = 'Search products...',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter available items (not selected)
  const availableItems = allItems.filter(item => !selectedItems.has(item.name));

  // Apply fuzzy search to available items
  const filteredItems = useFuzzySearch({
    data: availableItems,
    keys: ['name'],
    query: searchQuery,
    threshold: 0.4,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus input when dropdown opens
      setTimeout(() => inputRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (itemName: string) => {
    onToggleItem(itemName);
    // Keep dropdown open after selection
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Add Button */}
      {availableItems.length > 0 && (
        <button
          onClick={handleOpen}
          className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium px-3 py-1.5 border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
            <path d="M8 3.33334V12.6667M3.33334 8H12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('charts.addProducts')}</span>
          {availableItems.length > 0 && (
            <span className="text-[12px] text-[#667085] dark:text-textQuaternary-dark bg-gray-100 dark:bg-backgroundSecondary-dark px-1.5 py-0.5 rounded">
              {availableItems.length}
            </span>
          )}
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-card-dark border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-lg shadow-lg z-50 min-w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:min-w-[320px] sm:max-w-[400px] max-h-[400px] flex flex-col">
          {/* Search Input */}
          <div className="p-3 border-b border-[#E5E7EB] dark:border-border-dark">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#667085] dark:text-textQuaternary-dark pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-lg text-[14px] text-[#475467] dark:text-textSecondary-dark bg-white dark:bg-card-dark placeholder-[#9CA3AF] dark:placeholder-textQuaternary-dark focus:outline-none focus:ring-2 focus:ring-[#53B1FD] dark:focus:ring-[#53B1FD] focus:border-transparent"
              />
            </div>
          </div>

          {/* Items List */}
          <div className="overflow-y-auto max-h-[300px]">
            {filteredItems.length === 0 ? (
              <div className="p-4 text-center text-[#667085] dark:text-textQuaternary-dark text-[14px]">
                {searchQuery ? 'No products found' : 'No available products'}
              </div>
            ) : (
              <div className="p-1">
                {filteredItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleToggle(item.name)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-lg transition-colors flex items-center gap-2 group"
                  >
                    <div className="flex-1">
                      <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium group-hover:text-[#101828] dark:group-hover:text-textPrimary-dark">
                        {item.name}
                      </span>
                    </div>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <path
                        d="M8 3.33334V12.6667M3.33334 8H12.6667"
                        stroke="#53B1FD"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer with count */}
          {filteredItems.length > 0 && (
            <div className="px-3 py-2 border-t border-[#E5E7EB] dark:border-border-dark text-[12px] text-[#667085] dark:text-textQuaternary-dark">
              {filteredItems.length} {filteredItems.length === 1 ? 'product' : 'products'} available
            </div>
          )}
        </div>
      )}
    </div>
  );
};
